import type { Pipeline } from '@/lib/crm/hooks/usePipelines';

import {
  clearAllPersistedCacheByPrefix,
  getPersistedCache,
  setPersistedCache,
} from './persistedCache';

const CACHE_VERSION = 1;
const STORAGE_KEY_PREFIX = 'ahoy-cache-pipelines-';
const COMPONENT = 'persisted-pipelines';

function isPipelineStageShape(item: unknown): boolean {
  if (typeof item !== 'object' || item === null) {
    return false;
  }
  const s = item as Record<string, unknown>;
  if (
    typeof s.stage_name !== 'string' ||
    typeof s.label !== 'string' ||
    typeof s.description !== 'string'
  ) {
    return false;
  }
  if (s.entity_id !== null && typeof s.entity_id !== 'string') {
    return false;
  }
  // is_terminal is a required boolean on PipelineStageSchema. Reject snapshots
  // that predate the field (or carry a non-boolean) so stale entries are
  // invalidated at the cache boundary rather than rehydrated as stages that
  // violate the schema contract.
  if (typeof s.is_terminal !== 'boolean') {
    return false;
  }
  if (
    typeof s.properties !== 'object' ||
    s.properties === null ||
    Array.isArray(s.properties)
  ) {
    return false;
  }
  const props = s.properties as Record<string, unknown>;
  if (typeof props.order !== 'number') {
    return false;
  }
  if (
    props.probability !== undefined &&
    typeof props.probability !== 'number'
  ) {
    return false;
  }
  if (
    props.color !== undefined &&
    props.color !== null &&
    typeof props.color !== 'string'
  ) {
    return false;
  }
  return true;
}

function isPipelinesShape(raw: unknown): raw is Pipeline[] {
  if (!Array.isArray(raw)) {
    return false;
  }
  return raw.every(item => {
    if (typeof item !== 'object' || item === null) {
      return false;
    }
    const p = item as Record<string, unknown>;
    return (
      typeof p.pipeline_name === 'string' &&
      typeof p.label === 'string' &&
      typeof p.entity_type_name === 'string' &&
      // base_entity_type_name is optional on Pipeline; reject if present
      // with the wrong type so malformed cache entries don't slip through.
      (p.base_entity_type_name === undefined ||
        typeof p.base_entity_type_name === 'string') &&
      typeof p.description === 'string' &&
      typeof p.properties === 'object' &&
      p.properties !== null &&
      !Array.isArray(p.properties) &&
      Array.isArray(p.stages) &&
      p.stages.every(isPipelineStageShape) &&
      typeof p.is_default === 'boolean' &&
      (p.entity_id === null || typeof p.entity_id === 'string')
    );
  });
}

export function getPersistedPipelines(
  orgId: number | undefined
): Pipeline[] | undefined {
  if (orgId === undefined) {
    return undefined;
  }
  return getPersistedCache<Pipeline[]>(
    `${STORAGE_KEY_PREFIX}${orgId}`,
    CACHE_VERSION,
    isPipelinesShape,
    COMPONENT
  );
}

export function setPersistedPipelines(
  orgId: number,
  pipelines: Pipeline[]
): void {
  setPersistedCache(
    `${STORAGE_KEY_PREFIX}${orgId}`,
    pipelines,
    CACHE_VERSION,
    COMPONENT
  );
}

export function clearAllPersistedPipelines(): void {
  clearAllPersistedCacheByPrefix(STORAGE_KEY_PREFIX, COMPONENT);
}
